import { useEffect } from "react";

// Base pharmacy data used across all schemas
const PHARMACY_INFO = {
  name: "Wilberforce Pharmacy",
  alternateName: "My Local Pharmacy",
  url: "https://mylocalpharmacy.uk",
  telephone: "01482217494",
  email: "info@wilberforcepharmacy.co.uk",
  address: {
    "@type": "PostalAddress",
    streetAddress: "21 Story Street",
    addressLocality: "Hull",
    postalCode: "HU1 3SA",
    addressCountry: "GB",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 53.7457,
    longitude: -0.3367,
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "18:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: "Saturday",
      opens: "09:00",
      closes: "13:00",
    },
  ],
};

interface JsonLdProps {
  data: Record<string, unknown>;
}

function JsonLdScript({ data }: JsonLdProps) {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(data);
    script.id = `jsonld-${data["@type"]}`;
    
    // Remove existing script with same type if present
    const existing = document.getElementById(script.id);
    if (existing) {
      existing.remove();
    }
    
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById(script.id);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [data]);

  return null;
}

// LocalBusiness schema for homepage
export function LocalBusinessSchema() {
  const data = {
    "@context": "https://schema.org",
    "@type": "Pharmacy",
    "@id": "https://mylocalpharmacy.uk/#pharmacy",
    ...PHARMACY_INFO,
    image: "https://mylocalpharmacy.uk/favicon.ico",
    logo: "https://mylocalpharmacy.uk/favicon.ico",
    priceRange: "££",
    currenciesAccepted: "GBP",
    paymentAccepted: "Cash, Credit Card, Debit Card",
    areaServed: {
      "@type": "City",
      name: "Hull",
    },
    sameAs: [
      "https://www.facebook.com/wilberforcepharmacy",
      "https://www.instagram.com/wilberforcepharmacy",
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Pharmacy Services",
      itemListElement: [
        {
          "@type": "OfferCatalog",
          name: "NHS Services",
          itemListElement: [
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "NHS Flu Vaccination" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "COVID-19 Vaccination" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Pharmacy First" } },
          ],
        },
        {
          "@type": "OfferCatalog",
          name: "Private Services",
          itemListElement: [
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Travel Vaccinations" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Private Blood Tests" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Weight Loss Treatment" } },
          ],
        },
      ],
    },
  };

  return <JsonLdScript data={data} />;
}

// Organization schema for brand visibility
export function OrganizationSchema() {
  const data = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://mylocalpharmacy.uk/#organization",
    name: PHARMACY_INFO.name,
    alternateName: PHARMACY_INFO.alternateName,
    url: PHARMACY_INFO.url,
    logo: {
      "@type": "ImageObject",
      url: "https://mylocalpharmacy.uk/favicon.ico",
      width: 512,
      height: 512,
    },
    image: "https://mylocalpharmacy.uk/favicon.ico",
    description: "Wilberforce Pharmacy is a trusted community pharmacy in Hull offering NHS services, travel vaccinations, private blood tests, and weight management support.",
    telephone: PHARMACY_INFO.telephone,
    email: PHARMACY_INFO.email,
    address: PHARMACY_INFO.address,
    sameAs: [
      "https://www.facebook.com/wilberforcepharmacy",
      "https://www.instagram.com/wilberforcepharmacy",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      telephone: PHARMACY_INFO.telephone,
      contactType: "customer service",
      availableLanguage: ["English"],
      areaServed: "GB",
    },
    foundingLocation: {
      "@type": "Place",
      address: PHARMACY_INFO.address,
    },
    slogan: "Your trusted healthcare partner in Hull",
    knowsAbout: [
      "Travel Vaccinations",
      "Private Blood Tests",
      "NHS Pharmacy Services",
      "Weight Management",
      "Ear Care",
      "Pharmacy First",
    ],
  };

  return <JsonLdScript data={data} />;
}

// FAQ schema for pages with FAQs
interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSchemaProps {
  faqs: FAQItem[];
}

export function FAQSchema({ faqs }: FAQSchemaProps) {
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return <JsonLdScript data={data} />;
}

// Service schema for service detail pages
interface ServiceSchemaProps {
  name: string;
  description: string;
  url: string;
  price?: string;
  category?: string;
}

export function ServiceSchema({ name, description, url, price, category }: ServiceSchemaProps) {
  const data = {
    "@context": "https://schema.org",
    "@type": "MedicalService",
    name,
    description,
    url,
    provider: {
      "@type": "Pharmacy",
      name: PHARMACY_INFO.name,
      address: PHARMACY_INFO.address,
      telephone: PHARMACY_INFO.telephone,
    },
    areaServed: {
      "@type": "City",
      name: "Hull",
    },
    ...(price && {
      offers: {
        "@type": "Offer",
        price: price.replace(/[^0-9.]/g, ""),
        priceCurrency: "GBP",
      },
    }),
    ...(category && { serviceType: category }),
  };

  return <JsonLdScript data={data} />;
}

// Article schema for blog posts
interface ArticleSchemaProps {
  headline: string;
  description: string;
  url: string;
  datePublished: string;
  dateModified?: string;
  author?: string;
  image?: string;
}

export function ArticleSchema({
  headline,
  description,
  url,
  datePublished,
  dateModified,
  author = "Wilberforce Pharmacy",
  image,
}: ArticleSchemaProps) {
  const data = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline,
    description,
    url,
    datePublished,
    dateModified: dateModified || datePublished,
    author: {
      "@type": "Organization",
      name: author,
      url: PHARMACY_INFO.url,
    },
    publisher: {
      "@type": "Organization",
      name: PHARMACY_INFO.name,
      url: PHARMACY_INFO.url,
      logo: {
        "@type": "ImageObject",
        url: `${PHARMACY_INFO.url}/favicon.ico`,
      },
    },
    ...(image && { image }),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
  };

  return <JsonLdScript data={data} />;
}

// Medical Clinic schema for travel clinic
export function TravelClinicSchema() {
  const data = {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "@id": "https://mylocalpharmacy.uk/travel-clinic#clinic",
    name: "Wilberforce Pharmacy Travel Clinic",
    description: "Expert travel vaccinations in Hull. Yellow Fever centre, Hepatitis, Typhoid, Rabies and more. No GP referral needed.",
    url: "https://mylocalpharmacy.uk/travel-clinic",
    telephone: PHARMACY_INFO.telephone,
    email: PHARMACY_INFO.email,
    address: PHARMACY_INFO.address,
    geo: PHARMACY_INFO.geo,
    openingHoursSpecification: PHARMACY_INFO.openingHoursSpecification,
    medicalSpecialty: "Travel Medicine",
    availableService: [
      {
        "@type": "MedicalProcedure",
        name: "Yellow Fever Vaccination",
        procedureType: "Vaccination",
      },
      {
        "@type": "MedicalProcedure",
        name: "Hepatitis A Vaccination",
        procedureType: "Vaccination",
      },
      {
        "@type": "MedicalProcedure",
        name: "Typhoid Vaccination",
        procedureType: "Vaccination",
      },
      {
        "@type": "MedicalProcedure",
        name: "Rabies Vaccination",
        procedureType: "Vaccination",
      },
    ],
    parentOrganization: {
      "@type": "Pharmacy",
      name: PHARMACY_INFO.name,
      "@id": "https://mylocalpharmacy.uk/#pharmacy",
    },
  };

  return <JsonLdScript data={data} />;
}

// Breadcrumb schema
interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbSchemaProps {
  items: BreadcrumbItem[];
}

export function BreadcrumbSchema({ items }: BreadcrumbSchemaProps) {
  const data = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };

  return <JsonLdScript data={data} />;
}

// Vaccine/Medical Procedure schema for vaccine pages
interface VaccineSchemaProps {
  name: string;
  description: string;
  url: string;
  price: number;
  dosesRequired: string;
  destinations?: string[];
}

export function VaccineSchema({ name, description, url, price, dosesRequired, destinations }: VaccineSchemaProps) {
  const doses = parseInt(dosesRequired) || 1;
  const data = {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    name,
    description,
    url,
    procedureType: "Vaccination",
    howPerformed: "Injection administered by trained pharmacist",
    preparation: "Travel health consultation recommended 6-8 weeks before travel",
    followUp: doses > 1 ? `Full course requires ${dosesRequired} doses` : "Single dose provides protection",
    offers: {
      "@type": "Offer",
      price: price,
      priceCurrency: "GBP",
      availability: "https://schema.org/InStock",
    },
    provider: {
      "@type": "Pharmacy",
      name: PHARMACY_INFO.name,
      address: PHARMACY_INFO.address,
      telephone: PHARMACY_INFO.telephone,
    },
    ...(destinations && destinations.length > 0 && {
      relevantSpecialty: {
        "@type": "MedicalSpecialty",
        name: "Travel Medicine",
      },
    }),
  };

  return <JsonLdScript data={data} />;
}

// Travel destination schema
interface DestinationSchemaProps {
  name: string;
  region: string;
  description: string;
  url: string;
  vaccines: string[];
}

export function DestinationSchema({ name, region, description, url, vaccines }: DestinationSchemaProps) {
  const data = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: `Travel Vaccines for ${name}`,
    description,
    url,
    about: {
      "@type": "Place",
      name,
      containedInPlace: {
        "@type": "Continent",
        name: region,
      },
    },
    mainEntity: {
      "@type": "ItemList",
      name: `Recommended Vaccines for ${name}`,
      itemListElement: vaccines.map((vaccine, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: vaccine,
      })),
    },
    provider: {
      "@type": "MedicalClinic",
      name: "Wilberforce Pharmacy Travel Clinic",
      address: PHARMACY_INFO.address,
      telephone: PHARMACY_INFO.telephone,
    },
  };

  return <JsonLdScript data={data} />;
}

// Product schema for blood test profiles
interface ProductSchemaProps {
  name: string;
  description: string;
  url: string;
  price: string;
  testsIncluded: string[];
}

export function ProductSchema({ name, description, url, price, testsIncluded }: ProductSchemaProps) {
  const numericPrice = parseFloat(price.replace(/[^0-9.]/g, ""));
  
  const data = {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description,
    url,
    brand: {
      "@type": "Organization",
      name: PHARMACY_INFO.name,
    },
    offers: {
      "@type": "Offer",
      price: numericPrice,
      priceCurrency: "GBP",
      availability: "https://schema.org/InStock",
      seller: {
        "@type": "Pharmacy",
        name: PHARMACY_INFO.name,
        address: PHARMACY_INFO.address,
      },
    },
    category: "Medical Test",
    additionalProperty: testsIncluded.map((test) => ({
      "@type": "PropertyValue",
      name: "Test Included",
      value: test,
    })),
  };

  return <JsonLdScript data={data} />;
}

// ItemList schema for listing pages
interface ItemListSchemaProps {
  name: string;
  description: string;
  url: string;
  items: {
    name: string;
    url: string;
    price?: string;
    description?: string;
  }[];
}

export function ItemListSchema({ name, description, url, items }: ItemListSchemaProps) {
  const data = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    description,
    url,
    numberOfItems: items.length,
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Product",
        name: item.name,
        url: item.url,
        ...(item.description && { description: item.description }),
        ...(item.price && {
          offers: {
            "@type": "Offer",
            price: parseFloat(item.price.replace(/[^0-9.]/g, "")),
            priceCurrency: "GBP",
            availability: "https://schema.org/InStock",
          },
        }),
      },
    })),
  };

  return <JsonLdScript data={data} />;
}
